import { isMoveBeforeSupported } from './isMoveBeforeSupported.tsx';

type MoveBeforeCapable = HTMLElement & {
	moveBefore?: (node: Node, referenceNode: Node | null) => void;
};

/**
 * Imperatively relocate `node` so its parent becomes `targetParent` using
 * `Element.moveBefore()`, preserving any descendant iframe's load state.
 *
 * Returns true when the move ran (or was skipped because the node is already
 * in the right place), false otherwise. Deliberately does NOT fall back to
 * `appendChild` — that would reload descendant iframes.
 */
export const moveNodeBefore = (
	node: HTMLElement | null,
	targetParent: HTMLElement | null,
): boolean => {
	if (node == null || targetParent == null) {
		return false;
	}
	if (node.parentElement === targetParent) {
		return true;
	}
	if (!isMoveBeforeSupported()) {
		return false;
	}
	const target: MoveBeforeCapable = targetParent;
	if (typeof target.moveBefore !== 'function') {
		return false;
	}
	if (
		!node.isConnected ||
		!targetParent.isConnected ||
		node.ownerDocument !== targetParent.ownerDocument
	) {
		return false;
	}
	try {
		target.moveBefore(node, null);
		return true;
	} catch {
		return false;
	}
};
